'use client';

import { type ReactNode } from 'react';

import type { RenderLeafProps } from 'slate-react';

import type { CustomText } from '../types/SlateNodes';

/**
 * Slate `renderLeaf` dispatcher — text marks (bold / italic /
 * underline / strikethrough / code) compose by wrapping
 * progressively. Slate requires every leaf to render the
 * `attributes` on a `<span>` so cursor positioning works.
 *
 * `leaf` is cast through `unknown` because Slate's Text type is
 * augmented elsewhere in the workspace to a different shape — the
 * cast pins it to the composer's mark schema. Runtime structure
 * matches because the editor was wrapped with `withReact` only;
 * no other plugin touches marks.
 */
export function SlateEditorLeaf({
  attributes,
  children,
  leaf: rawLeaf,
}: RenderLeafProps) {
  const leaf = rawLeaf as unknown as CustomText;
  let node: ReactNode = children;
  if (leaf.bold) node = <strong>{node}</strong>;
  if (leaf.italic) node = <em>{node}</em>;
  if (leaf.underline) node = <u>{node}</u>;
  if (leaf.strikethrough) node = <s>{node}</s>;
  if (leaf.code) node = <code>{node}</code>;
  return <span {...attributes}>{node}</span>;
}
